/**
 * ProjectSourceD1.tsx
 * D1 Database data source provider for Rowy
 * Provides table CRUD operations via D1 API with Clerk authentication
 */

import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { useUser } from '@clerk/clerk-react';

import {
  projectScope,
  projectSettingsAtom,
  createTableAtom,
  updateTableAtom,
  deleteTableAtom,
  getTableSchemaAtom,
  AdditionalTableSettings,
  MinimumTableSettings,
} from '@src/atoms/projectScope';
import { TableSettings, TableSchema } from '@src/types/table';

/** D1 API endpoint from environment */
const D1_API_ENDPOINT = import.meta.env.VITE_D1_API_ENDPOINT || 'https://rowy-api.clayai.workers.dev';

/**
 * Get authentication token from Clerk
 */
async function getClerkToken(): Promise<string | null> {
  try {
    if (typeof window !== 'undefined' && window.Clerk) {
      // @ts-ignore
      const session = await window.Clerk.session;
      if (session) {
        return await session.getToken();
      }
    }
    return null;
  } catch (error) {
    console.error('Failed to get Clerk token:', error);
    return null;
  }
}

/**
 * Fetch tables from D1 API
 */
async function fetchTables(token: string | null): Promise<TableSettings[]> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${D1_API_ENDPOINT}/tables`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch tables');
  }

  const result = await response.json();
  return result.tables || [];
}

/**
 * Create table in D1
 */
async function createTableInD1(
  settings: TableSettings,
  additionalSettings?: AdditionalTableSettings,
  token?: string | null
): Promise<void> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${D1_API_ENDPOINT}/tables`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      settings,
      additionalSettings,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create table');
  }
}

/**
 * Update table in D1
 */
async function updateTableInD1(
  settings: MinimumTableSettings,
  additionalSettings?: AdditionalTableSettings,
  token?: string | null
): Promise<void> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${D1_API_ENDPOINT}/tables/${settings.id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({
      settings,
      additionalSettings,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update table');
  }
}

/**
 * Delete table from D1
 */
async function deleteTableFromD1(
  id: string,
  token?: string | null
): Promise<void> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${D1_API_ENDPOINT}/tables/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete table');
  }
}

/**
 * Get table schema from D1
 */
async function getTableSchemaFromD1(
  id: string,
  withSubtables?: boolean,
  token?: string | null
): Promise<TableSchema> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const params = new URLSearchParams();
  if (withSubtables) params.append('withSubtables', 'true');

  const response = await fetch(
    `${D1_API_ENDPOINT}/tables/${id}/schema?${params}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get table schema');
  }

  const result = await response.json();
  return result.schema || {};
}

/**
 * ProjectSourceD1 Component
 * Provides table CRUD operations via D1 API
 */
export default function ProjectSourceD1() {
  const { user, isLoaded } = useUser();

  const setProjectSettings = useSetAtom(projectSettingsAtom, projectScope);
  const setCreateTable = useSetAtom(createTableAtom, projectScope);
  const setUpdateTable = useSetAtom(updateTableAtom, projectScope);
  const setDeleteTable = useSetAtom(deleteTableAtom, projectScope);
  const setGetTableSchema = useSetAtom(getTableSchemaAtom, projectScope);

  // Load tables and set up CRUD functions
  useEffect(() => {
    if (!isLoaded) return;

    console.log('🚀 [ProjectSourceD1] Initializing D1 data source');
    console.log('👤 [ProjectSourceD1] User:', user?.id || 'none');

    // Set up create table function
    setCreateTable(() => async (settings: TableSettings, additionalSettings?: AdditionalTableSettings) => {
      console.log('📝 [ProjectSourceD1] Creating table:', settings.id);
      const token = await getClerkToken();
      await createTableInD1(settings, additionalSettings, token);

      // Refresh tables list
      const tables = await fetchTables(token);
      setProjectSettings((prev) => ({ ...prev, tables }));

      console.log('✅ [ProjectSourceD1] Table created successfully');
    });

    // Set up update table function
    setUpdateTable(() => async (settings: MinimumTableSettings, additionalSettings?: AdditionalTableSettings) => {
      console.log('📝 [ProjectSourceD1] Updating table:', settings.id);
      const token = await getClerkToken();
      await updateTableInD1(settings, additionalSettings, token);

      // Refresh tables list
      const tables = await fetchTables(token);
      setProjectSettings((prev) => ({ ...prev, tables }));

      console.log('✅ [ProjectSourceD1] Table updated successfully');
    });

    // Set up delete table function
    setDeleteTable(() => async (id: string) => {
      console.log('🗑️ [ProjectSourceD1] Deleting table:', id);
      const token = await getClerkToken();
      await deleteTableFromD1(id, token);

      // Refresh tables list
      const tables = await fetchTables(token);
      setProjectSettings((prev) => ({ ...prev, tables }));

      console.log('✅ [ProjectSourceD1] Table deleted successfully');
    });

    // Set up get table schema function
    setGetTableSchema(() => async (id: string, withSubtables?: boolean) => {
      console.log('📋 [ProjectSourceD1] Fetching schema for table:', id);
      const token = await getClerkToken();
      const schema = await getTableSchemaFromD1(id, withSubtables, token);
      console.log('✅ [ProjectSourceD1] Schema fetched successfully');
      return schema;
    });

    // Load initial tables list
    const loadTables = async () => {
      try {
        console.log('📥 [ProjectSourceD1] Loading tables...');
        const token = await getClerkToken();
        const tables = await fetchTables(token);
        setProjectSettings((prev) => ({ ...prev, tables }));
        console.log('✅ [ProjectSourceD1] Loaded', tables.length, 'tables');
      } catch (error) {
        console.error('❌ [ProjectSourceD1] Failed to load tables:', error);
        // Set empty tables array on error
        setProjectSettings((prev) => ({ ...prev, tables: [] }));
      }
    };

    loadTables();

    // Cleanup
    return () => {
      setCreateTable(undefined);
      setUpdateTable(undefined);
      setDeleteTable(undefined);
      setGetTableSchema(undefined);
    };
  }, [
    isLoaded,
    user,
    setProjectSettings,
    setCreateTable,
    setUpdateTable,
    setDeleteTable,
    setGetTableSchema,
  ]);

  return null;
}
